import { Button } from "@tokenterminal/core/ui/button/Button"

import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
import { Tabs } from "@tokenterminal/core/ui/navigation/tabs/Tabs"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { type ColumnApi } from "modules/datatable/DataGrid"
import { useEffect, useMemo, useRef, useState } from "react"

import { type MartPayloadData } from "../useMart"
import { SelectableColumnsList } from "./SelectableColumnsList"
import { SelectedColumnsList } from "./SelectedColumnsList"

export type ColumnItem = {
  col_id: string
  header_name: string
  field?: string | null
  children: MartPayloadData["column_definitions"]
}

export default function ColumnSelectorModal({
  selectedColumnIds,
  columnDefinitions,
  columnApi,
  buttonLabel,
  currentGroupByColumns,
  subtitle,
}: {
  selectedColumnIds: string[]
  columnDefinitions: MartPayloadData["column_definitions"]
  columnApi: ColumnApi
  buttonLabel: string
  currentGroupByColumns: string[]
  subtitle?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")

  // Capture the initial selected columns so we can reset to them when the modal is closed
  const defaultSelectedColumns = useRef<string[]>(selectedColumnIds)
  const [draftSelectedColumns, setDraftSelectedColumns] =
    useState<string[]>(selectedColumnIds)

  useEffect(() => {
    if (isOpen) {
      setDraftSelectedColumns(selectedColumnIds)
    }
  }, [selectedColumnIds, isOpen])

  const handleAddColumn = (field: string) => {
    setDraftSelectedColumns((prev) => [...prev, field])
  }

  const selectedItems = useMemo(() => {
    return draftSelectedColumns.map((c) => {
      const column = columnDefinitions.find((col) => col.col_id === c)
      const parentColumn = columnDefinitions.find(
        (col) => col.col_id === column?.parent
      )

      const children = columnDefinitions.filter(
        (col) => col.parent === parentColumn?.col_id
      )

      return {
        header_name: column?.header_name,
        field: c,
        parent_header_name: parentColumn?.header_name,
        parent: column?.parent,
        children: children || [],
      }
    })
  }, [columnDefinitions, draftSelectedColumns])

  const columns = useMemo(() => {
    return columnDefinitions.reduce((acc, item) => {
      if (item.parent) {
        // Find the parent item
        const parentItem = columnDefinitions.find(
          (col) => col.col_id === item.parent
        )
        if (parentItem) {
          // Find if parent already exists in accumulator
          const existingParentIndex = acc.findIndex(
            (group) => group.col_id === item.parent
          )

          if (existingParentIndex === -1) {
            // Add new parent with first child
            acc.push({ ...parentItem, children: [item] })
          } else {
            // Add child to existing parent
            acc[existingParentIndex].children = [
              ...(acc[existingParentIndex].children || []),
              item,
            ]
          }
        }
      } else if (item.field !== null) {
        acc.push({ ...item, children: [] })
      }
      return acc
    }, [] as ColumnItem[])
  }, [columnDefinitions])

  return (
    <>
      <Button onPress={() => setIsOpen(true)}>{buttonLabel}</Button>
      <Modal
        size="auto"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="max-h-[800px] h-[80vh] flex"
        hasDividers
      >
        <Modal.Header title="Edit columns" subtitle={subtitle} />
        <Modal.Body className="flex-1 w-full lg:w-[900px]">
          <div className="gap-5 h-full flex md:hidden">
            <Tabs
              className="w-full"
              tabs={[
                {
                  id: "all",
                  title: "All columns",
                  content: () => (
                    <div className="flex-1 flex flex-col gap-2 w-full">
                      <SearchField
                        placeholder="Search for a column"
                        value={search}
                        onChange={setSearch}
                        aria-label="Search for a column"
                      />
                      <div className="flex-1 overflow-y-auto">
                        <SelectableColumnsList
                          columns={columns}
                          search={search}
                          handleAddColumn={handleAddColumn}
                          draftSelectedColumns={draftSelectedColumns}
                        />
                      </div>
                    </div>
                  ),
                },
                {
                  id: "selected",
                  title: `Selected columns (${draftSelectedColumns.length + 1})`,
                  content: () => (
                    <div className="flex-[2.5] flex flex-col gap-2 max-h-[100vh]">
                      <SelectedColumnsList
                        setDraftSelectedColumns={setDraftSelectedColumns}
                        selectedItems={selectedItems}
                        draftSelectedColumns={draftSelectedColumns}
                        defaultSelectedColumns={defaultSelectedColumns}
                        currentGroupByColumns={currentGroupByColumns}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
          <div className="gap-5 h-full hidden md:flex">
            {/* Left column */}
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex items-center justify-between h-[20px] w-full">
                <div className="text-sm font-medium">All columns</div>
              </div>
              <SearchField
                placeholder="Search for a column"
                value={search}
                onChange={setSearch}
                aria-label="Search for a column"
              />
              <div className="flex-1 overflow-y-auto">
                <SelectableColumnsList
                  columns={columns}
                  search={search}
                  handleAddColumn={handleAddColumn}
                  draftSelectedColumns={draftSelectedColumns}
                />
              </div>
            </div>
            {/* Right column */}
            <div className="flex-[1.75] flex flex-col gap-2 max-h-[100vh]">
              <div className="flex items-center justify-between h-[20px] w-full">
                <div className="text-sm font-medium">
                  Selected columns ({draftSelectedColumns.length + 1})
                </div>
              </div>
              <SelectedColumnsList
                setDraftSelectedColumns={setDraftSelectedColumns}
                selectedItems={selectedItems}
                draftSelectedColumns={draftSelectedColumns}
                defaultSelectedColumns={defaultSelectedColumns}
                currentGroupByColumns={currentGroupByColumns}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onPress={() => setIsOpen(false)}>Cancel</Button>
          <Button
            variant="solid"
            onPress={() => {
              setIsOpen(false)

              setTimeout(() => {
                columnApi?.setColumnsVisible(
                  selectedColumnIds.map((item) => item),
                  false
                )

                columnApi?.setColumnsVisible(
                  currentGroupByColumns.map((item) => item),
                  true
                )
                columnApi?.removeRowGroupColumns(currentGroupByColumns)

                columnApi?.setColumnsVisible(
                  selectedItems.map((item) => item.field || ""),
                  true
                )

                columnApi?.moveColumns(
                  selectedItems.map((item) => item.field || ""),
                  0
                )

                if (currentGroupByColumns) {
                  const groupByColumnsToRestore = currentGroupByColumns.filter(
                    (colId) =>
                      selectedItems.some((item) => item.field === colId)
                  )

                  if (groupByColumnsToRestore.length > 0) {
                    columnApi?.setColumnsVisible(groupByColumnsToRestore, false)
                    columnApi?.addRowGroupColumns(groupByColumnsToRestore)
                  }
                }
              }, 0)
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
